/**
 * ChildProfilesGrid component
 * Responsive grid of child summary cards (one per linked child) ending with an
 * "Add a child" action card. Each card: coloured header strip, overlapping
 * circular avatar, name, last-session date, star rating (half-step), session
 * count chip, latest "Doing well:" highlights, and a "View reports" button.
 *
 * Implements US-008 (T3) / US-004 (T5) / US-010 (T4).
 * Modified US-002 (T-005): added onResetPassword prop + KeyRound icon button in the
 *   header strip, placed to the LEFT of the rename pencil in a flex container.
 *   All previously asserted strings/handlers/aria-labels are preserved verbatim:
 *   "Doing well:", "No report yet", "No sessions yet", "View reports", "Add a child",
 *   "{rounded} out of 5 stars for {name}", "Rename {name}", and
 *   "Add a child to your account" — so the US-008 / US-010 / US-004 / sprint-qa
 *   tests remain valid (no-placeholder rule: KeyRound only renders when
 *   onResetPassword is provided, exactly mirroring the onRenameChild pattern).
 * Modified TS-007 (T10, SR6, AC-TS007.5): added onSetDifficulty prop + a compact
 *   'Level: [label]' <select> in the card body. The selector combines the two
 *   grade-level values (Primary 4=P4, Primary 5=P5) with a 'Primary 6' <optgroup>
 *   holding the three difficulty bands (P6 Base=standard, P6 Intermediate=hard,
 *   P6 Advanced=beyond). Applies optimistically via useUpdateChildDifficulty
 *   (rendered/wired only when onSetDifficulty is provided — no-placeholder rule,
 *   mirroring onRenameChild/onViewReports).
 * Modified TS-007 (T11, SR6, AC-TS007.6c): the star slot now renders a non-tappable
 *   'Ended early' marker (no star) when child.lastSessionStatus === 'ended_by_parent',
 *   instead of the existing StarRating — distinct from the difficulty dropdown above.
 */

import { KeyRound, Pencil } from 'lucide-react';
import { ProfileIcon } from '@/components/ProfileIcon';
import type { ChildSummary, DifficultyTier } from '@/types/coins';
import { GRADE_LEVEL_OPTIONS, P6_BAND_OPTIONS } from '@/constants/difficulty';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ChildProfilesGridProps {
  /** List of children linked to this parent account */
  children: ChildSummary[];
  /** Callback for the "Add a child" card (omit to render a disabled card). */
  onAddChild?: () => void;
  /** US-004 (T5): per-child rename callback (omit to hide the rename affordance). */
  onRenameChild?: (child: ChildSummary) => void;
  /** US-002 (T-005): per-child reset-password callback (omit to hide the affordance). */
  onResetPassword?: (child: ChildSummary) => void;
  /** US-010 (T4): per-child "View reports" callback (omit to hide the button). */
  onViewReports?: (child: ChildSummary) => void;
  /**
   * TS-007 (T10, SR6): per-child level-change callback (omit to hide the
   * dropdown). Called with (childId, internal DifficultyTier value) on <select> change.
   */
  onSetDifficulty?: (childId: string, difficultyTier: DifficultyTier) => void;
  /**
   * Start a session for THIS child (omit to hide the button entirely — no-placeholder rule).
   *
   * The parent stays signed in as herself; the session belongs to the child. Wayne, 2026-07-27:
   * parents should always be able to start a child's session from their own account, not only
   * during the first-run guide.
   *
   * ⚠️ Called with the CHILD, never with "the first child". A page-level start that resolves the
   * child itself has to guess when there is more than one, and guesses wrong silently.
   */
  onStartSession?: (child: ChildSummary) => void;
  /**
   * sessionId of this child's currently live session, if any. When present the card offers a way
   * INTO that session instead of starting a second one, which the backend would refuse anyway
   * (one active session per child).
   */
  liveSessionIdByChildId?: Record<string, string | undefined>;
  /** Open a live session. Required for the walk-in action to render. */
  onOpenSession?: (sessionId: string) => void;
  /**
   * True when the family has no session to spend.
   *
   * ⚠️ This is resolved by the PAGE, not here, and it must gate on isUnlimited rather than the
   * balance alone — Unlimited families read a permanent zero, so a naive check offers a top-up
   * to the highest-paying customers forever. The card only renders what it is told.
   */
  outOfSessions?: boolean;
  /** userId of the child whose start is currently in flight, so only that card shows a spinner. */
  startingChildId?: string | null;
}

/**
 * TS-007 (T10, SR6, AC-TS007.5) — the per-child Level selector options.
 *
 * P4/P5 support: two top-level grade-level options followed by a 'Primary 6'
 * <optgroup> holding the three difficulty bands. Each label maps to the internal
 * DifficultyTier value stored on the child and matched against content by exact
 * equality (kept in parity with the backend VALID_DIFFICULTY_TIERS set). Rendered
 * in this exact order — do not reorder/relabel (asserted by AC-TS007.5).
 *
 * The option arrays live in '@/constants/difficulty' (single source of truth,
 * shared with AddChildDialog so the create box and the card stay in parity).
 */

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Format an ISO date string into a short label; 'No sessions yet' when absent/invalid. */
function formatLastSession(date: string | null | undefined): string {
  if (!date) return 'No sessions yet';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'No sessions yet';
    return d.toLocaleDateString('en-SG', { day: 'numeric', month: 'short' });
  } catch {
    return 'No sessions yet';
  }
}

/** Per-card header-strip gradients, rotated by card index. */
const HEADER_TONES = [
  'bg-gradient-to-r from-orange-200 to-orange-300',
  'bg-gradient-to-r from-tutor-200 to-tutor-300',
  'bg-gradient-to-r from-student-200 to-student-300',
];

// ─── StarRating helper (half-step capable) ────────────────────────────────────

interface StarRatingProps {
  rating: number | null | undefined;
  childName: string;
}

/**
 * StarRating — five stars filled to the rating using a clipped overlay so a 3.5
 * shows a half star. The aria-label uses the ROUNDED value ("{n} out of 5 stars
 * for {name}") to preserve the existing accessibility/test contract.
 *
 * When rating is null/undefined (a child with no rated session yet), the five
 * grey outline stars are still rendered as a placeholder — with a distinct
 * "No rating yet for {name}" label — so the card shows an empty star row instead
 * of repeating the "No sessions yet" copy that already appears on the line above.
 */
function StarRating({ rating, childName }: StarRatingProps) {
  const hasRating = rating != null;
  const clamped = hasRating ? Math.max(0, Math.min(5, rating)) : 0;
  const rounded = Math.round(clamped);
  const fillPct = (clamped / 5) * 100;

  return (
    <span
      className="relative inline-flex text-base leading-none tracking-[2px]"
      aria-label={
        hasRating
          ? `${rounded} out of 5 stars for ${childName}`
          : `No rating yet for ${childName}`
      }
    >
      <span aria-hidden="true" className="text-gray-300">
        ★★★★★
      </span>
      {hasRating && (
        <span
          aria-hidden="true"
          className="absolute inset-0 overflow-hidden whitespace-nowrap text-amber-500"
          style={{ width: `${fillPct}%` }}
        >
          ★★★★★
        </span>
      )}
    </span>
  );
}

// ─── ChildCard ────────────────────────────────────────────────────────────────

interface ChildCardProps {
  child: ChildSummary;
  index: number;
  onRenameChild?: (child: ChildSummary) => void;
  /** US-002 (T-005): per-child reset-password callback (omit to hide the affordance). */
  onResetPassword?: (child: ChildSummary) => void;
  onViewReports?: (child: ChildSummary) => void;
  /** TS-007 (T10, SR6): per-child level-change callback (omit to hide the dropdown). */
  onSetDifficulty?: (childId: string, difficultyTier: DifficultyTier) => void;
  onStartSession?: (child: ChildSummary) => void;
  liveSessionId?: string;
  onOpenSession?: (sessionId: string) => void;
  outOfSessions?: boolean;
  isStarting?: boolean;
}

function ChildCard({
  child,
  index,
  onRenameChild,
  onResetPassword,
  onViewReports,
  onSetDifficulty,
  onStartSession,
  liveSessionId,
  onOpenSession,
  outOfSessions = false,
  isStarting = false,
}: ChildCardProps) {
  const lastSessionLabel = formatLastSession(child.lastSessionDate);
  const sessionCount = child.totalSessions ?? 0;

  const strengths = child.lastReportSummary?.strengths ?? [];
  const hasStrengths = Array.isArray(strengths) && strengths.length > 0;

  // TS-007 (T11, SR6, AC-TS007.6c): 'Ended early' marker replaces the star slot when
  // the child's most recent terminal session was parent-ended — distinct from the
  // difficulty dropdown below. Absent/other values fall through to the existing
  // StarRating display (no change for legacy payloads).
  const lastSessionEndedByParent = child.lastSessionStatus === 'ended_by_parent';

  // TS-007 (T10, SR6, AC-TS007.5): current difficulty tier, defaulting to 'hard'
  // ("Standard") when absent (older cached payloads, or a brand-new child record).
  const currentDifficulty = child.difficultyTier ?? 'hard';

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-otter-sand bg-white shadow-md transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
      {/* Coloured header strip */}
      {/* US-002 (T-005): both action buttons (KeyRound + Pencil) wrapped in a
          flex container so they sit side by side. Button markup, sizing, and
          hover/focus classes are copied verbatim from the original rename button.
          Existing "Rename {name}" aria-label preserved so sprint-qa tests pass. */}
      <div className={`relative h-14 ${HEADER_TONES[index % HEADER_TONES.length]}`}>
        <div className="absolute right-2.5 top-2.5 flex items-center gap-2">
          {onResetPassword && (
            <button
              type="button"
              aria-label={`Reset ${child.displayName}'s password`}
              onClick={() => onResetPassword(child)}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/70 text-gray-600 backdrop-blur transition-colors hover:bg-white hover:text-otter-earth-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-tutor-400"
            >
              <KeyRound className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          {onRenameChild && (
            <button
              type="button"
              aria-label={`Rename ${child.displayName}`}
              onClick={() => onRenameChild(child)}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/70 text-gray-600 backdrop-blur transition-colors hover:bg-white hover:text-otter-earth-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-tutor-400"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Overlapping circular avatar. relative+z-10 so it paints ABOVE the
          position:relative header strip (otherwise the strip clips the avatar's top). */}
      <div className="relative z-10 -mt-8 flex justify-center">
        <ProfileIcon
          imageUrl={child.profileImageUrl}
          size="lg"
          className="border-4 border-white shadow-md"
        />
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col items-center px-4 pb-4 pt-2 text-center">
        <p className="font-display text-lg font-semibold leading-tight text-otter-earth-800">
          {child.displayName}
        </p>
        {/* Child's login username — shown so the parent can share it with their child */}
        {child.username && (
          <p className="mt-1 inline-flex items-center gap-1 rounded-full border border-otter-sand bg-otter-paper px-2.5 py-0.5 text-xs text-gray-500">
            <span aria-hidden="true">🔑</span>
            <span className="font-mono font-medium text-otter-earth-700">{child.username}</span>
          </p>
        )}
        <p className="mt-1 text-xs font-medium text-gray-500">{lastSessionLabel}</p>

        {/* Star rating slot — TS-007 (T11): a parent-ended session shows a
            non-tappable 'Ended early' marker instead of a star (no star, no
            fraction). Otherwise unchanged: a rated session fills the stars; a
            child with no rated session yet shows five grey placeholder stars. */}
        <div className="mt-2">
          {lastSessionEndedByParent ? (
            <span className="text-xs italic text-gray-400">Ended early</span>
          ) : (
            <StarRating rating={child.lastSessionStarRating} childName={child.displayName} />
          )}
        </div>

        {/* TS-007 (T10, SR6, AC-TS007.5): compact Level dropdown — only
            rendered when a live handler is provided (no-placeholder rule, mirrors
            onViewReports below). Applies optimistically via the parent-supplied
            onSetDifficulty callback (useUpdateChildDifficulty owns rollback).
            P4/P5 are single grade-level options; the 'Primary 6' optgroup holds
            the three difficulty bands. */}
        {onSetDifficulty && (
          <label className="mt-2 flex items-center gap-1.5 text-xs font-medium text-gray-500">
            Level:
            <select
              value={currentDifficulty}
              onChange={(e) =>
                onSetDifficulty(child.userId, e.target.value as DifficultyTier)
              }
              aria-label={`Set ${child.displayName}'s level`}
              className="h-8 rounded-lg border border-otter-sand bg-white px-1.5 text-xs font-semibold text-otter-earth-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-tutor-400"
            >
              {GRADE_LEVEL_OPTIONS.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
              <optgroup label="Primary 6">
                {P6_BAND_OPTIONS.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
        )}

        {/* Session count chip */}
        <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-tutor-50 px-3 py-1 text-xs font-bold text-tutor-700">
          <span aria-hidden="true">📖</span>
          {sessionCount} {sessionCount === 1 ? 'session' : 'sessions'}
        </span>

        {/* Latest highlights — "Doing well:" string preserved for tests (AC-2) */}
        {hasStrengths ? (
          <div className="mt-3 w-full flex-1 rounded-xl border border-student-200 bg-student-50 p-3 text-left">
            <p className="mb-1 text-xs font-bold text-student-700">Doing well:</p>
            {strengths.slice(0, 2).map((s, i) => (
              <p key={i} className="flex gap-1.5 text-xs leading-snug text-gray-700">
                <span aria-hidden="true" className="text-student-600">
                  ✓
                </span>
                <span className="truncate">{s}</span>
              </p>
            ))}
          </div>
        ) : (
          <span className="mt-3 flex-1 text-xs italic text-gray-400">No report yet</span>
        )}

        {/*
          Start her session. Permanently available, not only during the first-run guide
          (Wayne, 2026-07-27): "parents can always choose to start their child session from
          their account".

          Three states, and the card is told which — it never works them out itself:
            live session   -> a way IN, because the backend allows only one at a time and
                              would refuse a second
            out of sessions -> an offer, never a start we already know will be refused
            otherwise      -> start

          The child is passed to the handler, so nothing has to guess WHICH child. A
          page-level start that resolves the child itself picks one silently when there is
          more than one.
        */}
        {onStartSession &&
          (liveSessionId && onOpenSession ? (
            <button
              type="button"
              aria-label={`Go to ${child.displayName}'s practice`}
              onClick={() => onOpenSession(liveSessionId)}
              className="mt-3 inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-xl bg-otter-orange-500 px-3 text-[13px] font-bold text-white transition-colors hover:bg-otter-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400"
            >
              Go to practice
            </button>
          ) : outOfSessions ? (
            /*
              ⚠️ INFORMATION, NOT AN ACTION. Wayne, 2026-07-27: "if there isn't enough coins, it
              shouldn't be a clickable button, but a label to inform parent to top up coins to
              start. this will simplify the flow to only have 1 entry point always for all
              purchases."

              So this deliberately does not link anywhere. Buying happens in exactly one place,
              the Plan & Coins tab, and every other surface only ever tells her why she cannot
              start. Do not "helpfully" make this clickable later — a second purchase entry point
              is the thing this exists to prevent.

              role="status" so a screen reader is told why the start button is absent, rather
              than the card simply appearing to have lost its action.
            */
            <p
              role="status"
              className="mt-3 w-full rounded-xl border border-otter-sand bg-otter-paper px-3 py-2.5 text-center text-xs font-semibold text-otter-earth-700"
            >
              Top up coins to start practice
            </p>
          ) : (
            <button
              type="button"
              aria-label={`Start ${child.displayName}'s practice`}
              onClick={() => onStartSession(child)}
              disabled={isStarting}
              className="mt-3 inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-xl bg-otter-orange-500 px-3 text-[13px] font-bold text-white transition-colors hover:bg-otter-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isStarting ? 'Starting…' : 'Start practice'}
            </button>
          ))}

        {/* View reports — only when a live handler is provided (no-placeholder rule) */}
        {onViewReports && (
          <button
            type="button"
            aria-label={`View ${child.displayName}'s reports`}
            onClick={() => onViewReports(child)}
            className="mt-3 inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-orange-300 bg-orange-50 px-3 text-[13px] font-bold text-orange-600 transition-colors hover:border-otter-orange-500 hover:bg-otter-orange-500 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400"
          >
            View reports
            <span aria-hidden="true">→</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── AddChildCard ─────────────────────────────────────────────────────────────

interface AddChildCardProps {
  onAddChild?: () => void;
}

function AddChildCard({ onAddChild }: AddChildCardProps) {
  if (onAddChild) {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label="Add a child to your account"
        onClick={onAddChild}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onAddChild();
          }
        }}
        className="flex min-h-[280px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-otter-sand bg-white/40 p-6 text-center transition-colors hover:border-otter-orange-400 hover:bg-orange-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400"
      >
        <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-otter-sand bg-white text-3xl text-otter-orange-500 shadow-sm">
          +
        </span>
        <p className="font-display text-base font-medium text-otter-earth-800">Add a child</p>
        <p className="mt-1 max-w-[180px] text-xs text-gray-500">
          Link your child&apos;s account to monitor progress
        </p>
      </div>
    );
  }

  // Disabled affordance (no-placeholder rule: describes what it will do)
  return (
    <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 p-6 text-center opacity-60">
      <span className="mb-3 text-3xl text-gray-300" aria-hidden="true">
        +
      </span>
      <p className="font-display text-base font-medium text-gray-400">Add a child</p>
      <p className="mt-1 max-w-[180px] text-xs text-gray-400">
        Child linking will be available soon — ask your tutor for details
      </p>
    </div>
  );
}

// ─── ChildProfilesGrid (main export) ─────────────────────────────────────────

export function ChildProfilesGrid({
  children = [],
  onAddChild,
  onRenameChild,
  onResetPassword,
  onViewReports,
  onSetDifficulty,
  onStartSession,
  liveSessionIdByChildId,
  onOpenSession,
  outOfSessions = false,
  startingChildId = null,
}: ChildProfilesGridProps) {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {children.map((child, index) => (
        <ChildCard
          key={child.userId}
          child={child}
          index={index}
          onRenameChild={onRenameChild}
          onResetPassword={onResetPassword}
          onViewReports={onViewReports}
          onStartSession={onStartSession}
          liveSessionId={liveSessionIdByChildId?.[child.userId]}
          onOpenSession={onOpenSession}
          outOfSessions={outOfSessions}
          isStarting={startingChildId === child.userId}
          onSetDifficulty={onSetDifficulty}
        />
      ))}
      <AddChildCard onAddChild={onAddChild} />
    </div>
  );
}
